import { AppElement, html } from '../AppElement.js';
import TotalPickupTitle from "../totalPickupExpandCard/TotalPickupTitle";
import PickupPerMonthHotel from "../pickupPerMonth/PickupPerMonthHotel";
import FlashExpandCard from "../expand/FlashExpandCard";
import Button from '../button/Button';
import Constants from "../Constants";
import TotalPickupHotelTrend from "../totalPickupExpandCard/TotalPickupHotelTrend";
import TotalPickupExpandCardInfo from "../totalPickupExpandCard/TotalPickupExpandCardInfo";
import {calcDuration} from '../Utils';
import TopPickupHotelCardList from "../topPickup/TopPickupHotelCardList";
import MiniContext from "../miniContext/MiniContext";
import YesterdayHotel from "../yesterday/YesterdayHotel";
import NumericVarianceBlock from "../numericVarianceBlock/NumericVarianceBlock";

export default class FlashGroupHotelExpandCard extends AppElement {
    static get properties() {
        return {
            hotel: { type: Object },
            recordDate: { type: String },
            pickupSetting: { type: Number }
        };
    }

    constructor(props = {}) {
        super();

        this.id = AppElement.getUniqueElementId();

        this.hotel = props.hotel || {};
        this.recordDate = props.recordDate;
        this.pickupSetting = props.pickupSetting;

        this.card = this.createCard();
    }

    reflow() {
        this.hotel = props.hotel || this.hotel;
        this.recordDate = props.recordDate || this.recordDate;
        this.pickupSetting = props.pickupSetting || this.pickupSetting;

        this.card = this.createCard();
        this.fill();
    }

    fill() {
        if (
            !this.hotel
            || typeof this.hotel !== 'object'
            || !('id' in this.hotel)
            || !window.infinito.vao.controller.dateHelper.isDateStr(this.recordDate)
            || !$.isNumeric(this.pickupSetting)
        ) {
            return;
        }

        // Read the tile from BI rather than V2 flashgroupapi. BI leaves the statistic value null
        // when either the record or its offset record is missing, so a property that stopped
        // sending PMS data renders "No Data" instead of a fabricated pickup (INF-1437).
        const biStatistics = window.infinito.vao.model.biStatistics;
        const flag = this;

        biStatistics.fetchStatistics(
            this.hotel.id,
            biStatistics.buildQuery({
                recordDate: this.recordDate,
                fields: [
                    biStatistics.fields.roomsOtbTotalPickup,
                    biStatistics.fields.adrTotalPickup,
                    biStatistics.fields.revenueTotalPickup
                ],
                pickupOffset: this.pickupSetting
            }),
            function onStatistics(data, procData) {
                const _procData = procData || {};
                const roomsOtbTotalPickup = _procData.roomsOtbTotalPickup || {};
                const roomsOtbTotalPickupVal = roomsOtbTotalPickup.value;
                const adrTotalPickupVal = (_procData.adrTotalPickup || {}).value;
                const revenueTotalPickupVal = (_procData.revenueTotalPickup || {}).value;

                // All three fields resolve from the same pair of snapshots, so they are present
                // together or absent together. Absent means no data, not zero.
                const hasAnyValue = $.isNumeric(roomsOtbTotalPickupVal)
                    || $.isNumeric(adrTotalPickupVal)
                    || $.isNumeric(revenueTotalPickupVal);

                if (!hasAnyValue) {
                    if (flag.card instanceof FlashExpandCard && flag.card.contentEl instanceof TotalPickupTitle) {
                        flag.card.contentEl.reflow({
                            noData: true,
                            isLoading: false
                        });
                    }
                    return;
                }

                let otb;
                if ($.isNumeric(roomsOtbTotalPickupVal)) {
                    otb = new NumericVarianceBlock({
                        numericValue: roomsOtbTotalPickupVal,
                        formattedValue: String(roomsOtbTotalPickupVal),
                        tooltip: Constants.STRINGS.ROOMS
                    });
                } else {
                    otb = Constants.MISSING_STR;
                }

                let adr;
                if ($.isNumeric(adrTotalPickupVal)) {
                    const formattedAdr = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        adrTotalPickupVal,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    adr = new NumericVarianceBlock({
                        numericValue: adrTotalPickupVal,
                        formattedValue: formattedAdr,
                        tooltip: Constants.STRINGS.ADR
                    });
                } else {
                    adr = Constants.MISSING_STR;
                }

                let rev;
                if ($.isNumeric(revenueTotalPickupVal)) {
                    const formattedRev = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        revenueTotalPickupVal,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rev = new NumericVarianceBlock({
                        numericValue: revenueTotalPickupVal,
                        formattedValue: formattedRev,
                        tooltip: Constants.STRINGS.REV
                    });
                } else {
                    rev = Constants.MISSING_STR;
                }

                const duration = calcDuration(roomsOtbTotalPickup);

                if (flag.card instanceof FlashExpandCard && flag.card.contentEl instanceof TotalPickupTitle) {
                    flag.card.contentEl.reflow({
                        otb,
                        adr,
                        rev,
                        duration,
                        noData: false,
                        isLoading: false
                    });
                }
            }
        );
    }

    onCardHelp() {
        window.infinito.components.basicModal.render({
            title: 'Skeleton',
            bodyElem: $(new TotalPickupExpandCardInfo())
        });
    }

    createCard() {
        const content = new TotalPickupTitle({
            name: this.hotel.name,
            isLoading: true
        });

        const action = new TotalPickupHotelTrend({
            hotel: this.hotel,
            pickupSetting: this.pickupSetting,
            recordDate: this.recordDate
        });

        const pickupPerMonthHotel = new PickupPerMonthHotel({
            hotel: this.hotel,
            recordDate: this.recordDate,
            pickupSetting: this.pickupSetting
        });

        const topPickups = new TopPickupHotelCardList({
            hotel: this.hotel,
            recordDate: this.recordDate,
            pickupSetting: this.pickupSetting
        });

        const jumpBtn = new Button({
            text: 'Jump',
            startIcon: Constants.ICONS.JUMP,
            size: 'small',
            variant: 'invert',
            onClick: () => {
                window.infinito.vao.controller.storageHelper.changeSelectedHotel(this.hotel);
                window.infinito.vao.controller.storageHelper.changeAppMode('single');
            }
        });

        const yesterdayBtn = new Button({
            text: 'Yesterday',
            color: 'light',
            size: 'small',
            onClick: () => {
                const yesterdayHotel = new YesterdayHotel({
                    recordDate: this.recordDate,
                    hotel: this.hotel
                });
                const prettyRecordDate = window.infinito.vao.controller.dateHelper
                    .getShortDayDateMonFullYearString(this.recordDate);
                const prettyStayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(
                    yesterdayHotel.yesterday
                );
                const $title =  $('<div></div>').append(
                    '<h5>Yesterday</h5>'
                ).append(new MiniContext({
                    recordDate: prettyRecordDate,
                    stayDate: prettyStayDate,
                    isModalStyle: false
                }));
                window.infinito.components.basicModal.render({
                    title: $title,
                    isTitleHtml: true,
                    bodyElem: $(yesterdayHotel)
                });
            }
        });

        const expandBtnsTarget = `#${this.id} .vao__components--flashGroupHotelExpandCard-topPickups`;
        const expand = html`
            <div 
                class="vao__components--flashGroupHotelExpandCard-btns" 
                style="padding:.5rem 0;display:flex;justify-content:flex-end;"
            >
                <div style="margin-right:1rem;">${yesterdayBtn}</div>
                <div>${jumpBtn}</div>     
            </div>
            <h6 class="vao__components--flashGroup-bodySectionTitle">
                Top Pickups
                <span style="display:inline-block;">
                    <vao-expand-btns .targetSelector="${expandBtnsTarget}"></vao-expand-btns>
                </span>
            </h6>
            <div class="vao__components--flashGroupHotelExpandCard-topPickups">${topPickups}</div>
            <br>
            ${pickupPerMonthHotel} 
        `;

        return new FlashExpandCard({
            actionEl: action,
            hotel_id:this.hotel,
            contentEl: content,
            expandEl: expand,
            onHelp: 'tooltips',
            isExpanded: false
        });
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.fill();
    }

    render() {
        return html`
        <div class="vao__components--flashGroupHotelExpandCard">
            ${this.card}     
        </div>
        `;
    }
}

window.vao = window.vao || {};
window.vao.components = window.vao.components || {};
window.vao.components.FlashGroupHotelExpandCard = FlashGroupHotelExpandCard;
customElements.define('vao-flash-group-hotel-expand-card', FlashGroupHotelExpandCard);
